import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import { BetaChartCard } from 'src/pages/reports/components/beta-charts';
import { BetaChartAreaSkeleton } from 'src/pages/reports/components/beta-charts/beta-chart-skeletons';
import { BetaChartTooltip } from 'src/pages/reports/components/beta-charts/beta-chart-tooltip';
import {
  formatCompactNumber,
  formatPlatformDisplayName,
  formatRatePercent,
} from './overview-beta-format';

export interface PlatformComparisonRow {
  name: string;
  displayName: string;
  colour: string;
  spend: number;
  impressions: number;
  clicks: number;
  conversions: number;
  ctr: number;
}

interface OverviewBetaPlatformComparisonProps {
  data: PlatformComparisonRow[];
  isLoading?: boolean;
}

export function OverviewBetaPlatformComparison({
  data,
  isLoading = false,
}: OverviewBetaPlatformComparisonProps) {
  const spendData = [...data].sort((a, b) => b.spend - a.spend);
  const ctrData = [...data].sort((a, b) => b.ctr - a.ctr);
  const scatterData = data.map((row) => ({
    ...row,
    x: row.spend,
    y: row.ctr,
    z: Math.max(row.impressions, 1),
  }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <BetaChartCard
          title="Spend by Platform"
          subtitle="Enabled platforms this period"
          className="mb-0"
        >
          {isLoading ? (
            <BetaChartAreaSkeleton className="h-[240px]" />
          ) : spendData.length === 0 ? (
            <div className="flex h-[240px] items-center justify-center text-sm text-[#8A8EA0]">
              No platform spend data.
            </div>
          ) : (
            <div className="h-[240px] w-full">
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <BarChart
                  data={spendData}
                  layout="vertical"
                  margin={{ left: 8, right: 12 }}
                >
                  <CartesianGrid
                    stroke="#EDEEF2"
                    horizontal={false}
                  />
                  <XAxis
                    type="number"
                    tick={{ fill: '#8A8EA0', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(value) => formatCompactNumber(Number(value), { currency: true })}
                  />
                  <YAxis
                    type="category"
                    dataKey="displayName"
                    width={110}
                    tick={{ fill: '#5E6278', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    content={(
                      <BetaChartTooltip
                        valueFormatter={(value) => formatCompactNumber(value, { currency: true })}
                      />
                    )}
                    cursor={{ fill: 'rgba(37, 99, 235, 0.06)' }}
                  />
                  <Bar
                    dataKey="spend"
                    name="Spend"
                    radius={[0, 4, 4, 0]}
                    isAnimationActive={false}
                  >
                    {spendData.map((row) => (
                      <Cell
                        key={row.name}
                        fill={row.colour}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </BetaChartCard>

        <BetaChartCard
          title="CTR by Platform"
          subtitle="Click-through rate comparison"
          className="mb-0"
        >
          {isLoading ? (
            <BetaChartAreaSkeleton className="h-[240px]" />
          ) : ctrData.length === 0 ? (
            <div className="flex h-[240px] items-center justify-center text-sm text-[#8A8EA0]">
              No platform CTR data.
            </div>
          ) : (
            <div className="h-[240px] w-full">
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <BarChart
                  data={ctrData}
                  layout="vertical"
                  margin={{ left: 8, right: 12 }}
                >
                  <CartesianGrid
                    stroke="#EDEEF2"
                    horizontal={false}
                  />
                  <XAxis
                    type="number"
                    tick={{ fill: '#8A8EA0', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(value) => `${Number(value).toFixed(2)}%`}
                  />
                  <YAxis
                    type="category"
                    dataKey="displayName"
                    width={110}
                    tick={{ fill: '#5E6278', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    content={(
                      <BetaChartTooltip
                        valueFormatter={(value) => formatRatePercent(value, 3)}
                      />
                    )}
                    cursor={{ fill: 'rgba(37, 99, 235, 0.06)' }}
                  />
                  <Bar
                    dataKey="ctr"
                    name="CTR"
                    radius={[0, 4, 4, 0]}
                    isAnimationActive={false}
                  >
                    {ctrData.map((row) => (
                      <Cell
                        key={row.name}
                        fill={row.colour}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </BetaChartCard>
      </div>

      <BetaChartCard
        title="Efficiency Map — CTR vs Spend"
        subtitle="Bubble size = impressions. Higher CTR at lower spend is more efficient."
        controls={(
          <div className="flex flex-wrap gap-3">
            {data.map((row) => (
              <div
                key={row.name}
                className="flex items-center gap-1.5 text-xs font-semibold text-[#5E6278]"
              >
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: row.colour }}
                />
                {formatPlatformDisplayName(row.displayName)}
              </div>
            ))}
          </div>
        )}
      >
        {isLoading ? (
          <BetaChartAreaSkeleton className="h-[260px]" />
        ) : scatterData.length === 0 ? (
          <div className="flex h-[260px] items-center justify-center text-sm text-[#8A8EA0]">
            No efficiency data for the selected period.
          </div>
        ) : (
          <div className="h-[260px] w-full">
            <ResponsiveContainer
              width="100%"
              height="100%"
            >
              <ScatterChart margin={{ top: 12, right: 16, left: 8, bottom: 8 }}>
                <CartesianGrid stroke="#EDEEF2" />
                <XAxis
                  type="number"
                  dataKey="x"
                  name="Spend"
                  tick={{ fill: '#8A8EA0', fontSize: 11 }}
                  tickFormatter={(value) => formatCompactNumber(Number(value), { currency: true })}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  type="number"
                  dataKey="y"
                  name="CTR"
                  tick={{ fill: '#8A8EA0', fontSize: 11 }}
                  tickFormatter={(value) => `${Number(value).toFixed(2)}%`}
                  axisLine={false}
                  tickLine={false}
                  width={48}
                />
                <ZAxis
                  type="number"
                  dataKey="z"
                  range={[80, 400]}
                />
                <Tooltip
                  cursor={{ strokeDasharray: '3 3' }}
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const point = payload[0]?.payload as PlatformComparisonRow & {
                      x: number;
                      y: number;
                    };
                    return (
                      <div className="rounded-lg border border-[#E8E9ED] bg-white px-3 py-2.5 shadow-[0_4px_16px_rgba(13,15,26,0.10)]">
                        <p className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.4px] text-[#8A8EA0]">
                          {formatPlatformDisplayName(point.displayName)}
                        </p>
                        <div className="space-y-1 text-[12px] font-semibold text-[#5E6278]">
                          <div className="flex justify-between gap-4">
                            <span>
                              Spend
                            </span>
                            <span className="font-bold text-[#0D0F1A]">
                              {formatCompactNumber(point.x, { currency: true })}
                            </span>
                          </div>
                          <div className="flex justify-between gap-4">
                            <span>
                              CTR
                            </span>
                            <span className="font-bold text-[#0D0F1A]">
                              {formatRatePercent(point.y, 3)}
                            </span>
                          </div>
                          <div className="flex justify-between gap-4">
                            <span>
                              Impressions
                            </span>
                            <span className="font-bold text-[#0D0F1A]">
                              {formatCompactNumber(point.impressions)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  }}
                />
                <Scatter
                  data={scatterData}
                  isAnimationActive={false}
                >
                  {scatterData.map((row) => (
                    <Cell
                      key={row.name}
                      fill={row.colour}
                    />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        )}
      </BetaChartCard>
    </div>
  );
}
